import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatInstantWithZone } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Invoice, InvoiceEmail } from '@/routes/invoices/useInvoices';

/** The outcomes that mean the email did not arrive (the Event Webhook's word, M2-16 pass A). */
const UNDELIVERED = new Set(['Bounced', 'Dropped']);

/** Outcomes that are a problem the office should look at — styled as such. */
const PROBLEM = new Set(['Bounced', 'Dropped', 'Failed']);

/**
 * Every email about this invoice and what became of it (M2-16, pass B): the
 * invoice email, each resend and each reminder, oldest first, exactly as the
 * API lists them (`emails`). This is where a bounce surfaces instead of
 * disappearing — the AC's last bullet.
 *
 * - `outcome` is one word from a closed list, shown as a label. A word this
 *   page does not know yet is shown as the API spelt it, never dropped.
 * - `reason` for Bounced/Dropped/Failed is the provider's closed vocabulary
 *   or the sanitised error — shown as it comes, office-only. For NotSent it is
 *   a code (`no-address`, `sandbox`, …), shown as a sentence.
 * - When the NEWEST email bounced or was dropped, a note above the list says
 *   so and what to do: check the address, then resend. An older bounce
 *   followed by a delivery is history, not a problem.
 *
 * ⚠️ Each time is ONE instant — delivery if the webhook reported one, else
 * when the provider took it, else when it was queued — on the ACCOUNT's
 * clock, date and time from one formatter with the zone named (the "one
 * clock" rule). Until the account's zone is known the time is left out
 * rather than shown on the viewer's clock.
 */
export function InvoiceEmails({
  invoice,
  timeZone,
}: {
  invoice: Invoice;
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();
  const id = useId();
  const emails = invoice.emails;

  if (invoice.status === 'Draft' && emails.length === 0) return null;

  const latest = emails.at(-1);

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('invoices.emails.heading')}
      </h2>
      {latest && UNDELIVERED.has(latest.outcome) ? (
        <p
          className="rounded-md border border-[var(--color-destructive)] px-3 py-2 text-sm"
          data-testid="email-undelivered"
        >
          {t('invoices.emails.undelivered', {
            name: invoice.customerName,
            outcome: outcomeLabel(t, latest.outcome).toLowerCase(),
          })}
        </p>
      ) : null}
      {emails.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('invoices.emails.empty')}</p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] text-sm">
          {emails.map((email, index) => {
            const when = email.deliveryAt ?? email.sentAt ?? email.queuedAt;
            const reason = reasonText(t, email);
            return (
              <li
                // The API gives an email no id; the list is append-only and
                // oldest first, so the position is a stable key.
                key={index}
                className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-2"
                data-testid="invoice-email"
                data-outcome={email.outcome}
              >
                <div className="min-w-0">
                  <p className="font-medium" data-testid="email-kind">
                    {kindLabel(t, email)}
                  </p>
                  {timeZone ? (
                    <p className="text-[var(--color-muted-foreground)]" data-testid="email-when">
                      {formatInstantWithZone(when, timeZone)}
                    </p>
                  ) : null}
                  {reason ? (
                    <p className="text-[var(--color-muted-foreground)]" data-testid="email-reason">
                      {reason}
                    </p>
                  ) : null}
                </div>
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-xs font-medium',
                    PROBLEM.has(email.outcome)
                      ? 'border-[var(--color-destructive)] text-[var(--color-destructive)]'
                      : 'border-[var(--color-border)]',
                  )}
                  data-testid="email-outcome"
                >
                  {outcomeLabel(t, email.outcome)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function kindLabel(t: TFunction, email: InvoiceEmail): string {
  if (email.kind !== 'Reminder') return t('invoices.emails.kindInvoice');
  const step = email.step === null || email.step === undefined ? null : Number(email.step);
  if (step === null) return t('invoices.emails.reminder');
  return step < 0
    ? t('invoices.emails.reminderBefore', { count: -step })
    : t('invoices.emails.reminderAfter', { count: step });
}

function outcomeLabel(t: TFunction, outcome: string): string {
  return t(`invoices.emails.outcome.${outcome}`, { defaultValue: outcome });
}

function reasonText(t: TFunction, email: InvoiceEmail): string | null {
  if (!email.reason) return null;
  if (email.outcome === 'NotSent') {
    return t(`invoices.emails.notSent.${email.reason}`, { defaultValue: email.reason });
  }
  return email.reason;
}
